// src/components/ws/WsSessions.jsx
// 出席記録（全画面）の「回ごと」タブ【2026-10-13】
//
//   左に回の一覧、右に選んだ回の詳細。
//   もとは表の中に行が開いていた（メモ・日付の修正・当てはまる学生）が、行が伸び縮みして
//   どこを見ていたか分からなくなるので、右の欄にまとめた。
//   消すボタンは右の欄のいちばん下に離して置く（押し間違い防止）。
//   API は もとの出席記録（ClassSummaryPane）と同じ。
import React, { useEffect, useMemo, useState } from 'react';
import WsSplit from './WsSplit';
import { useColWidths, ColGrip } from './useColWidths';
import { useNavigate } from 'react-router-dom';
import { StudentList, rowsForSession } from '../SummaryDrill';
import { fmtDate, fmtMD, fmtClock, fmtWeekday, rateColor, byDateAsc } from './wsCommon';
import { confirmDialog, promptDialog } from '../ConfirmDialog';

const STATUS_LABEL = { active: '受付中', completed: '終了', cancelled: '休講', scheduled: '予定' };

export default function WsSessions({ classId, data, matrix, focus, readOnly, auth, setErr, setMsg, onChanged }) {
  const navigate = useNavigate();
  const [selId, setSelId]   = useState(null);
  // 先生が回を押した回数。横の欄を畳んでいても、押したら開く（WsSplit の openSignal）
  const [pickN, setPickN]   = useState(0);
  const pick = (id) => { setSelId(id); setPickN(n => n + 1); };
  const [drill, setDrill]   = useState(null);     // kind
  const [draft, setDraft]   = useState('');
  const [saving, setSaving] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveDate, setMoveDate] = useState('');
  const [moveSlot, setMoveSlot] = useState('');
  const [slots, setSlots]   = useState(null);

  const enrolled = Number((data && data.overview && data.overview.enrolled_students) || 0);
  // 古い順に並べる（概要の棒グラフ・出欠表と同じ向き）
  const rows = useMemo(() => ((data && data.sessions) || []).slice().sort(byDateAsc), [data]);
  // 列の幅（エクセルのように見出しの境目をドラッグ）【2026-10-13】
  const COLS = [['no', '回'], ['date', '日付'], ['time', '時刻'], ['status', '状態'], ['att', '出席'], ['late', '遅刻'],
                ['early', '早退'], ['abs', '欠席'], ['out', '名簿外'], ['rate', '出席率'], ['memo', 'メモ']];
  const colKeys = useMemo(() => COLS.map(c => c[0]), []); // eslint-disable-line react-hooks/exhaustive-deps
  const cols = useColWidths('sessions', colKeys, { defaultWidth: k => ({ no: 70, date: 150, time: 130, status: 60, rate: 160, memo: 260 }[k] || 64) });
  const noOf = useMemo(() => {
    const m = new Map();
    ((matrix && matrix.sessions) || []).forEach(s => m.set(s.id, s.no));
    return m;
  }, [matrix]);
  const dupDays = useMemo(() => {
    const c = {};
    rows.filter(s => s.status !== 'cancelled').forEach(s => { const k = s.session_ymd || fmtDate(s.session_date); c[k] = (c[k] || 0) + 1; });
    return c;
  }, [rows]);

  // 最初に選ぶ回：概要の棒から来たらその回、なければ最後に実施した回
  useEffect(() => {
    if (focus && focus.session && rows.some(s => s.id === focus.session)) { setSelId(focus.session); return; }
    if (selId && rows.some(s => s.id === selId)) return;
    const last = [...rows].reverse().find(s => s.status !== 'cancelled');
    setSelId(last ? last.id : (rows[0] ? rows[0].id : null));
  }, [focus, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const sel = rows.find(s => s.id === selId) || null;
  useEffect(() => {
    setDraft(sel ? (sel.lesson_notes || '') : '');
    setMoving(false); setDrill(null);
  }, [selId, sel && sel.lesson_notes]); // eslint-disable-line react-hooks/exhaustive-deps

  const numbers = (s) => {
    const cancelled = s.status === 'cancelled';
    const attended = Number(s.attended_count || 0);
    const absent = (!cancelled && enrolled > 0) ? Math.max(enrolled - attended, 0) : null;
    const rate = (!cancelled && enrolled > 0) ? Math.round(attended * 1000 / enrolled) / 10 : null;
    return { cancelled, attended, absent, rate };
  };

  const saveNotes = async () => {
    if (!sel) return;
    setSaving(true); setErr('');
    try {
      const res = await fetch('/api/class-summary/sessions/' + sel.id + '/notes', {
        method: 'PUT', headers: auth(), body: JSON.stringify({ notes: draft })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status));
      setMsg('メモを保存しました');
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const openMove = async () => {
    if (!sel) return;
    setMoving(true);
    setMoveDate(sel.session_ymd || fmtDate(sel.session_date));
    setMoveSlot(sel.schedule_id == null ? '' : String(sel.schedule_id));
    if (slots === null) {
      try {
        const r = await fetch('/api/schedule/classes/' + classId + '/schedules', { headers: auth() });
        const d = await r.json();
        setSlots(r.ok && d.schedules ? d.schedules : []);
      } catch (e) { setSlots([]); }
    }
  };

  const saveMove = async () => {
    const s = sel;
    const body = {};
    const oldDate = s.session_ymd || fmtDate(s.session_date);
    const oldSlot = s.schedule_id == null ? '' : String(s.schedule_id);
    if (moveDate && moveDate !== oldDate) body.date = moveDate;
    if (moveSlot !== oldSlot) body.scheduleId = moveSlot === '' ? null : Number(moveSlot);
    if (!Object.keys(body).length) { setMoving(false); return; }
    if (body.date && Number(s.attended_count) + Number(s.unenrolled_count || 0) > 0 &&
        !(await confirmDialog(`この回の出席の記録も ${body.date.replace(/-/g, '/')} の記録になります。直しますか？`))) return;
    setSaving(true); setErr('');
    try {
      const res = await fetch('/api/class-summary/sessions/' + s.id, { method: 'PUT', headers: auth(), body: JSON.stringify(body) });
      const d = await res.json();
      if (!res.ok || !d.success) {
        if (d.code === 'SESSION_CONFLICT' && d.conflictSessionId &&
            (await confirmDialog((d.message || 'その日・その時限の回がもうあります') + '\n\n［OK］回をまとめる画面を開く'))) {
          navigate('/classes/' + classId + '/merge?keep=' + d.conflictSessionId);
          return;
        }
        throw new Error(d.message || d.error || ('HTTP ' + res.status));
      }
      setMsg([d.message || '直しました'].concat(d.warnings || []).join('　'));
      setMoving(false);
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  /** 1回ぶんを出席記録ごと消す（授業そのものは残す）。確認はもとの出席記録と同じ2段階 */
  const deleteSession = async () => {
    const s = sel;
    const label = fmtDate(s.session_date);
    const send = (body) => fetch('/api/class-summary/sessions/' + s.id, {
      method: 'DELETE', headers: auth(), ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    try {
      const r1 = await send();
      const d1 = await r1.json();
      if (d1.code !== 'CONFIRM_REQUIRED') throw new Error(d1.message || d1.error || '消せませんでした');
      const n = d1.count || 0;
      const cands = d1.mergeCandidates || [];
      if (n > 0 && cands.length > 0) {
        const goMerge = (await confirmDialog(
          `${label} にはほかにも回があります（${cands.map(c => (c.start || '時刻なし') + '・記録' + c.records + '件').join(' / ')}）。\n\n` +
          '二度押しや開き直しで回が分かれただけなら、消すのではなく「回をまとめる」で1つにすると、出席記録を失わずに済みます。\n\n' +
          '［OK］回をまとめる画面を開く　［キャンセル］このまま削除に進む'));
        if (goMerge) { navigate('/classes/' + classId + '/merge?keep=' + cands[0].id); return; }
      }
      if (n > 0) {
        if (!(await confirmDialog(`${label} の回を消します。\n\nこの回の出席記録 ${n} 件（うち出席・遅刻・早退 ${d1.attended || 0} 件）も一緒に消えます。\n授業そのものは残ります。元には戻せません。\n\n本当に消しますか？`))) return;
        const typed = (await promptDialog(`確認のため、消える出席記録の件数（${n}）を半角数字で入力してください。`, ''));
        if (typed === null) return;
        if (String(typed).trim() !== String(n)) { setErr('件数が違うので、消すのをやめました'); return; }
      } else if (!(await confirmDialog(`${label} の回を消します。\n授業そのものは残ります。元には戻せません。\n\n本当に消しますか？`))) {
        return;
      }
      const r2 = await send({ confirmRecords: n, acknowledgeRecords: n > 0 });
      const d2 = await r2.json();
      if (!r2.ok || !d2.success) throw new Error(d2.message || d2.error || '消せませんでした');
      setMsg(d2.message || '消しました'); setErr('');
      setSelId(null);
      onChanged();
    } catch (e) { setErr(e.message); }
  };

  if (!data) return <div className="ws-panel"><p className="ws-muted">読み込み中…</p></div>;
  if (rows.length === 0) return <div className="ws-panel"><p className="ws-muted">まだ授業を実施していません。</p></div>;

  const n = sel ? numbers(sel) : null;
  const drillTitle = { attended: '出席した学生', late: '遅刻した学生', early_leave: '早退した学生', absent: '欠席した学生', outsiders: '名簿にいない学生の打刻' };

  return (
    <WsSplit id="sessions" label="選んだ回" openSignal={pickN || null}>
      <div className="ws-split-main">
        <div className="ws-table-box">
          {cols.active && (
            <div style={{ textAlign: 'right', padding: '0.3rem 0.6rem 0', position: 'sticky', left: 0 }}>
              <button type="button" className="ws-link-btn" onClick={cols.resetAll} title="ドラッグで変えた列の幅を、全部元に戻す">列の幅を戻す</button>
            </div>
          )}
          <table ref={cols.tableRef} className={'ws-ss ' + cols.className} style={cols.tableStyle}>
            {cols.colgroup}
            <thead>
              <tr>
                {COLS.map(([k, label]) => (
                  <th key={k} className={['att', 'late', 'early', 'abs', 'out'].includes(k) ? 'ws-num' : undefined}
                      style={k === 'rate' && !cols.active ? { minWidth: 140 } : undefined}
                      title={k === 'out' ? '打刻はあるが、履修者名簿に載っていない学生の数' : undefined}>
                    {label}<ColGrip cols={cols} k={k} label={label} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(s => {
                const x = numbers(s);
                const day = s.session_ymd || fmtDate(s.session_date);
                const dup = !x.cancelled && dupDays[day] >= 2;
                return (
                  <tr key={s.id} className={(s.id === selId ? 'is-sel' : '') + (x.cancelled ? ' is-dim' : '')}
                      onClick={() => pick(s.id)}>
                    <td className="ws-muted">{noOf.has(s.id) ? '第' + noOf.get(s.id) + '回' : '—'}</td>
                    <td>
                      <button type="button" className="ws-name-btn" onClick={(e) => { e.stopPropagation(); pick(s.id); }}>
                        {fmtMD(s.session_date)}{fmtWeekday(s.session_date)}
                      </button>
                      {dup && <span className="ws-chip-warn">同じ日に{dupDays[day]}回</span>}
                    </td>
                    <td className="ws-muted">
                      {fmtClock(s.start_time) || '—'}
                      {s.status === 'active' ? '〜受付中' : fmtClock(s.ended_at) ? '〜' + fmtClock(s.ended_at) : ''}
                    </td>
                    <td className="ws-muted">{STATUS_LABEL[s.status] || s.status}</td>
                    <td className="ws-num">{x.cancelled ? '—' : x.attended}</td>
                    <td className="ws-num">{x.cancelled ? '—' : s.late_count}</td>
                    <td className="ws-num">{x.cancelled ? '—' : s.early_leave_count}</td>
                    <td className="ws-num">{x.absent == null ? '—' : x.absent}</td>
                    <td className="ws-num" style={Number(s.unenrolled_count) > 0 ? { color: '#9a3412', fontWeight: 700 } : undefined}>
                      {x.cancelled ? '—' : Number(s.unenrolled_count) || 0}
                    </td>
                    <td>
                      {x.rate == null ? <span className="ws-muted">—</span> : (
                        <div className="ws-rate-bar">
                          <div><i style={{ width: Math.min(x.rate, 100) + '%', background: rateColor(x.rate) }} /></div>
                          <span>{x.rate}%</span>
                        </div>
                      )}
                    </td>
                    <td className="ws-memo-cell">{s.lesson_notes ? s.lesson_notes.replace(/\s+/g, ' ') : <span className="ws-muted">（メモなし）</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <aside className="ws-split-side" aria-label="選んだ回">
        {!sel ? <p className="ws-muted">左の一覧から回を選んでください。</p> : (
          <>
            <div>
              <div className="ws-muted" style={{ fontSize: '0.8rem' }}>
                {noOf.has(sel.id) ? '第' + noOf.get(sel.id) + '回' : (STATUS_LABEL[sel.status] || '')}
              </div>
              <h2 className="ws-side-title">
                {fmtDate(sel.session_date).replace(/-/g, '/')}{fmtWeekday(sel.session_date)}
                <span className="ws-muted" style={{ fontSize: '0.95rem', fontWeight: 400, marginLeft: '0.4rem' }}>
                  {fmtClock(sel.start_time)}{sel.status === 'active' ? '〜受付中' : fmtClock(sel.ended_at) ? '〜' + fmtClock(sel.ended_at) : ''}
                </span>
              </h2>
            </div>

            {!n.cancelled && (
              <div className="ws-side-stats">
                {[['attended', '出席', n.attended], ['late', '遅刻', sel.late_count], ['early_leave', '早退', sel.early_leave_count],
                  ['absent', '欠席', n.absent == null ? '—' : n.absent], ['outsiders', '名簿外', Number(sel.unenrolled_count) || 0]].map(([k, l, v]) => (
                  <button key={k} type="button" className={'ws-side-stat' + (drill === k ? ' on' : '') + (k === 'absent' ? ' is-abs' : '')}
                          onClick={() => setDrill(d => (d === k ? null : k))} title="押すと当てはまる学生が出ます">
                    <span>{l}</span><b>{v}</b>
                  </button>
                ))}
              </div>
            )}
            {drill && (
              <StudentList title={`${fmtMD(sel.session_date)} の${drillTitle[drill]}`}
                           rows={matrix ? rowsForSession(matrix, sel.id, drill) : []}
                           loading={!matrix} classId={classId} onClose={() => setDrill(null)} />
            )}

            <label className="ws-field">
              <span>授業メモ</span>
              <textarea rows={4} value={draft} onChange={e => setDraft(e.target.value)} maxLength={10000}
                        placeholder="授業内容・連絡事項などを記録" />
            </label>
            {sel.lesson_notes_updated_at && (
              <div className="ws-muted" style={{ fontSize: '0.78rem', marginTop: '-0.4rem' }}>
                更新: {String(sel.lesson_notes_updated_at).slice(0, 16).replace('T', ' ')}
              </div>
            )}
            <div className="ws-side-actions-row">
              <button type="button" className="btn-primary" disabled={saving || draft === (sel.lesson_notes || '')} onClick={saveNotes}>
                {saving ? '保存中…' : 'メモを保存'}
              </button>
              {sel.status !== 'active' && !readOnly && (
                <button type="button" className="btn-secondary" onClick={() => (moving ? setMoving(false) : openMove())}>日付・時限を直す</button>
              )}
              {sel.status !== 'cancelled' && rows.length >= 2 && !readOnly && (
                <button type="button" className="btn-secondary" onClick={() => navigate('/classes/' + classId + '/merge?keep=' + sel.id)}>
                  ほかの回をまとめる…
                </button>
              )}
            </div>

            {moving && (
              <div className="ws-edit-box">
                <div className="ws-muted" style={{ fontSize: '0.85rem' }}>出席の記録も一緒に動きます（打刻した時刻はそのまま）</div>
                <label className="ws-inline-field">日付
                  <input type="date" value={moveDate} onChange={e => setMoveDate(e.target.value)} />
                </label>
                <label className="ws-inline-field">時限
                  <select value={moveSlot} onChange={e => setMoveSlot(e.target.value)}>
                    <option value="">時限なし</option>
                    {(slots || []).map(sl => (
                      <option key={sl.id} value={String(sl.id)}>
                        {(sl.day_name || '') + ' ' + String(sl.start_time || '').slice(0, 5) + '〜' + String(sl.end_time || '').slice(0, 5)}
                      </option>
                    ))}
                  </select>
                </label>
                {slots === null && <span className="ws-muted" style={{ fontSize: '0.8rem' }}>時限を読み込み中…</span>}
                <div className="ws-side-actions-row">
                  <button type="button" className="btn-primary" disabled={saving || !moveDate} onClick={saveMove}>{saving ? '保存中…' : '直す'}</button>
                  <button type="button" className="btn-secondary" disabled={saving} onClick={() => setMoving(false)}>やめる</button>
                </div>
              </div>
            )}

            {sel.status !== 'active' && !readOnly && (
              <div className="ws-danger-zone">
                <button type="button" className="btn-danger" onClick={deleteSession}>この回を出席記録ごと消す…</button>
                <div className="ws-muted" style={{ fontSize: '0.78rem' }}>授業そのものは残ります。元には戻せません。</div>
              </div>
            )}
          </>
        )}
      </aside>
    </WsSplit>
  );
}
